import React from 'react';
import {ComposedChart, Line, Area, XAxis, ResponsiveContainer, LabelList, CartesianGrid} from 'recharts';
import {BIOMARKER_STATUS_COLORS} from '@/shared/constants/colors';

// Lead-in/out (as a fraction of one inter-point gap) from the first/last
// data point to the chart border in design mode. Shared with LabResults so
// its external date labels line up with the chart's inset points.
export const DESIGN_LEAD = 0.3;

interface TestingHistoryDataPoint {
    name: string;
    value: number | null;
    dummy?: boolean;
    // Internal flags used by the navyDots design grid:
    // `edge` points extend the area/line flat to the chart borders (no dot,
    // no label, no vertical line); `current` marks the latest real reading;
    // `x` is the numeric x-position used so the lead-in/out to the borders
    // is short (a fraction of a gap) rather than a full category width.
    edge?: boolean;
    current?: boolean;
    x?: number;
}

interface TestingHistoryProps {
    data: TestingHistoryDataPoint[];
    title?: string;
    height?: number;
    showGrid?: boolean;
    showVerticalLines?: boolean;
    simpleMode?: boolean;
    thresholds?:
        | {
              low: number;
              high: number;
          }
        | {
              excellent: number;
              good: number;
          };
    className?: string;
    labelFontSize?: string;
    /**
     * Hide the chart's built-in X axis. Used when the caller renders its own
     * axis labels outside a framed chart (e.g. Lab Results "Result History",
     * where the dates sit below the bordered chart with the current one boxed).
     * Defaults to true so existing usages are unchanged.
     */
    showXAxis?: boolean;
    /**
     * Render uniform navy data points (the latest as a ringed "target")
     * instead of threshold status colors — matches the v5 design's Result
     * History dots. Defaults to false so existing usages keep status colors.
     */
    navyDots?: boolean;
}

const TestingHistory: React.FC<TestingHistoryProps> = ({
    data,
    title = 'Testing History',
    height = 200,
    showGrid = true,
    showVerticalLines = false,
    simpleMode = false,
    thresholds,
    className = '',
    labelFontSize = '16px',
    showXAxis = true,
    navyDots = false,
}) => {
    // In design mode (navyDots) the real points get numeric x = 0..N-1 and
    // two flat "edge" points sit a short fraction (DESIGN_LEAD) of a gap
    // past the ends, so the area + line run to the left/right borders with
    // only a small lead-in — the real points stay inset (dots/labels/
    // vertical-lines never clip the border). The last real point is `current`.
    const realValues = data.filter((d) => !d.dummy && d.value !== null);
    const designN = realValues.length;
    const designLead = designN > 1 ? DESIGN_LEAD : 0.5;
    const designDomain: [number, number] = [-designLead, (designN - 1 || 0) + designLead];
    const chartData: TestingHistoryDataPoint[] =
        navyDots && designN >= 1
            ? [
                  {name: '__edgeL', x: designDomain[0], value: realValues[0].value, edge: true},
                  ...realValues.map((d, i) => ({...d, x: i, current: i === designN - 1})),
                  {name: '__edgeR', x: designDomain[1], value: realValues[designN - 1].value, edge: true},
              ]
            : data;
    // Indices of the border-extension points (first + last) in design mode,
    // so the value LabelList (which isn't passed the data payload) can skip them.
    const edgeIndices = navyDots ? new Set([0, chartData.length - 1]) : new Set<number>();

    // Custom dot component that colors based on value and thresholds
    const CustomDot = (props: any) => {
        const {cx, cy, payload} = props;

        // Don't render anything for edge (border-extension) or dummy points.
        if (payload.edge || payload.dummy) return null;
        // Don't render a dot for a non-numeric reading (value === null, e.g.
        // "Test Not Performed"). The line already gaps it; without this guard
        // the dot leaks to the chart edge. Mirrors the CustomLabel null guard
        // below (and matches the prod fix from PR #53).
        if (payload.value === null) return null;

        // Uniform navy dots (design's Result History); the latest point is a
        // ringed "target". Each dot also drops a faint vertical line from the
        // point DOWN to the chart bottom — no horizontal grid, and the line
        // never extends above the curve. Drawn before threshold coloring.
        if (navyDots) {
            const isCurrent = payload.current === true;
            const bottomY = height; // plot bottom (margin bottom 0, axis hidden)
            return (
                <g>
                    <line x1={cx} y1={cy} x2={cx} y2={bottomY} stroke="#E5E7EB" strokeWidth={1} />
                    {isCurrent ? (
                        <>
                            <circle cx={cx} cy={cy} r={5.5} fill="#FFFFFF" stroke="#013B4B" strokeWidth={1.5} />
                            <circle cx={cx} cy={cy} r={2.5} fill="#013B4B" />
                        </>
                    ) : (
                        <circle cx={cx} cy={cy} r={4} fill="#013B4B" stroke="none" />
                    )}
                </g>
            );
        }

        let color: string = BIOMARKER_STATUS_COLORS.NORMAL; // Default green for normal values

        if (thresholds && payload.value !== null) {
            if ('excellent' in thresholds) {
                // Heart health 3-tier system
                if (payload.value >= thresholds.excellent) {
                    color = BIOMARKER_STATUS_COLORS.NORMAL; // Green for excellent
                } else if (payload.value >= thresholds.good) {
                    color = BIOMARKER_STATUS_COLORS.LOW; // Orange for good
                } else {
                    color = '#EF4444'; // Red for poor
                }
            } else {
                // Reference range defines normal values
                if (payload.value < thresholds.low) {
                    color = BIOMARKER_STATUS_COLORS.LOW; // Orange for low (below range)
                } else if (payload.value > thresholds.high) {
                    color = BIOMARKER_STATUS_COLORS.HIGH; // Pink for high (above range)
                } else {
                    color = BIOMARKER_STATUS_COLORS.NORMAL; // Green for normal (within range)
                }
            }
        }

        return <circle cx={cx} cy={cy} r={8} fill={color} stroke="none" />;
    };

    // Custom label component for values above circles
    const CustomLabel = (props: any) => {
        const {x, y, value, payload, index} = props;

        // Don't render labels for edge (border-extension) or dummy points.
        // LabelList doesn't pass `payload`, so edges are skipped by index.
        if (edgeIndices.has(index)) return null;
        if (payload && (payload.edge || payload.dummy)) return null;
        if (value === null) return null;

        // Always use navy color for value labels
        const color = '#334155'; // Lighter navy color for all value labels

        return (
            <text
                x={x}
                y={y - 15}
                fill={color}
                textAnchor="middle"
                style={{
                    fontFamily: 'Poppins',
                    fontSize: labelFontSize,
                    fontWeight: 400,
                    lineHeight: 'auto',
                    letterSpacing: '0%',
                }}>
                {typeof value === 'number'
                    ? (() => {
                          const rounded = Number(Number(value).toFixed(2));
                          return rounded === Math.floor(rounded) ? rounded.toString() : rounded.toString();
                      })()
                    : value}
            </text>
        );
    };

    return (
        <div className={`${className}`}>
            {title && (
                <h3
                    className="text-[24px] font-normal text-mld-card-text mb-4 text-center"
                    style={{fontFamily: 'Poppins', fontWeight: 400, lineHeight: 'auto', letterSpacing: '0%'}}>
                    {title}
                </h3>
            )}

            <div className="relative">
                <div className="w-full">
                    <ResponsiveContainer width="100%" height={height}>
                        <ComposedChart
                            data={chartData}
                            margin={
                                simpleMode
                                    ? {top: 20, right: 20, left: 20, bottom: 0}
                                    : navyDots
                                      ? {top: 40, right: 0, left: 0, bottom: 0}
                                      : {top: 40, right: 30, left: 30, bottom: 0}
                            }>
                            {/* Recharts grid - like staging. Suppressed in design
                                mode: navyDots draws its own per-point vertical
                                lines (via the dots) with no horizontal grid. */}
                            {showGrid && !navyDots && (
                                <CartesianGrid
                                    strokeDasharray="0"
                                    stroke="#E9E9E4"
                                    strokeWidth={1}
                                    vertical={showVerticalLines}
                                    horizontal={true}
                                />
                            )}

                            <XAxis
                                dataKey={navyDots ? 'x' : 'name'}
                                type={navyDots ? 'number' : 'category'}
                                domain={navyDots ? designDomain : undefined}
                                tick={{fill: '#013B4B', fontSize: 12}}
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                hide={!showXAxis}
                                padding={navyDots ? {left: 0, right: 0} : undefined}
                            />

                            {/* Blue gradient area fill */}
                            <defs>
                                <linearGradient id="heartHealthGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05} />
                                </linearGradient>
                            </defs>

                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke="none"
                                fill="url(#heartHealthGradient)"
                                connectNulls={false}
                            />

                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke={simpleMode ? '#42D4B0' : navyDots ? '#013B4B' : '#334155'}
                                strokeWidth={simpleMode ? 2 : navyDots ? 1.5 : 1}
                                dot={simpleMode ? {r: 4, fill: '#42D4B0'} : <CustomDot />}
                                activeDot={simpleMode ? {r: 6, fill: '#42D4B0'} : false}>
                                {!simpleMode && <LabelList content={<CustomLabel />} />}
                            </Line>
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default TestingHistory;
